import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useApiQuery } from '../../hooks/useApi';
import { formatDateSafe } from '../../utils/dateUtils';
import { ReporteProvider, useReporte, reporteActions } from '../../context/ReporteContext';
import { 
  Button, 
  Card, 
  LoadingSpinner,
  ErrorState 
} from '../../components/ui';
import { ReportesService } from '../../services';
import { ApiClient } from '../../services/apiClient';
import FormularioReporteCompleto from '../../components/reportes/FormularioReporteCompleto';

// Tipos para la respuesta del backend (GET /reportes/{id})
interface ReporteCompleto {
  id: number;
  numero: string;
  fecha: string;
  maquina_id: number;
  operador_id?: number;
  horometro_inicial: number;
  horometro_final: number;
  observaciones?: string;
  estado_maquina: string;
  novedades?: string;
  estado: string; // PENDIENTE, APROBADO, etc.
  
  // Relaciones
  maquina?: {
    id: number;
    codigo: string;
    tipo: string;
    descripcion: string;
  };
  operador?: {
    id: number;
    codigo: string;
    nombre: string;
  };
  faenas: ReporteFaenaCompleta[];
  insumos: ReporteInsumoCompleto[];
  items_chequeo: ReporteItemChequeoCompleto[];
}

interface ReporteFaenaCompleta {
  id: number;
  faena_id: number;
  predio_id?: number;
  rodal_id?: number;
  horas: number;
  area: number;
  
  // Datos directos en la respuesta
  faena_codigo: string;
  faena_descripcion: string;
  predio_codigo: string;
  predio_descripcion: string;
  rodal_codigo: string;
  rodal_descripcion: string;
  
  // Archivos KML asociados (si los hay)
  archivos_kml?: ArchivoKmlCompleto[];
}

interface ArchivoKmlCompleto {
  id: number;
  nombre_archivo_original: string;
  area_hectareas: number;
  longitud_total_metros?: number; // ✅ NUEVO campo para longitud
  ruta_almacenamiento: string;
}

interface ReporteInsumoCompleto {
  id: number;
  insumo_id: number;
  cantidad: number;
  
  // Datos del insumo directamente en la respuesta
  insumo_codigo: string;
  insumo_descripcion: string;
  unidad_medida: string;
}

interface ReporteItemChequeoCompleto {
  id: number;
  itemchequeo_id: number;
  horometro: number;
  tiempo: number;
  observaciones?: string;

  // ✅ CAMPOS PLANOS del backend (via @hybrid_property)
  item_codigo: string;
  item_descripcion: string;
  categoria: string;
}

const EditarReporteView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const reporteId = id ? parseInt(id, 10) : null;

  if (!reporteId) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <ErrorState 
          message="ID de reporte inválido" 
          actionText="Volver a Reportes"
          onAction={() => window.location.href = '/reportes'}
        />
      </div>
    );
  }

  return (
    <ReporteProvider>
      <EditarReporteContent reporteId={reporteId} />
    </ReporteProvider>
  );
};

// Componente interno que usa el contexto
interface EditarReporteContentProps {
  reporteId: number;
}

const EditarReporteContent: React.FC<EditarReporteContentProps> = ({ reporteId }) => {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { dispatch } = useReporte();

  useState(true); // modoEdicion
  const [, setReporteOriginal] = useState<ReporteCompleto | null>(null);
  const [datosPreCargados, setDatosPreCargados] = useState(false);

  // Cargar reporte completo desde el backend
  const { 
    data: reporte, 
    isLoading: loadingReporte, 
    error: errorReporte,
    refetch: refetchReporte  // 🆕 NUEVO: Función para forzar recarga
  } = useApiQuery(
    ['reporte-completo', reporteId],
    () => reporteId ? ReportesService.getReporte(reporteId) : Promise.resolve(null),
    {
      enabled: !!reporteId,
      staleTime: 0,  // 🆕 NUEVO: No usar cache - siempre recargar
      cacheTime: 0,  // 🆕 NUEVO: No guardar en cache
    }
  );

  // Convertir geometría WKT a coordenadas para el mapa
  const convertirWKTACoordenadas = (wktGeometria: string): [number, number][] => {
    try {
      // Extraer coordenadas del POLYGON WKT
      // Formato: "POLYGON((-71.278 -33.693, -71.279 -33.693, ...))"
      const match = wktGeometria.match(/POLYGON\(\(([^)]+)\)\)/);
      if (!match) {
        throw new Error('Formato WKT no válido');
      }
      
      const coordenadasString = match[1];
      const puntosWKT = coordenadasString.split(',');
      
      // Convertir cada punto "lng lat" a [lng, lat]
      const coordenadas = puntosWKT.map(punto => {
        const [lng, lat] = punto.trim().split(' ').map(Number);
        return [lng, lat] as [number, number];
      });
      
      return coordenadas;
    } catch (error) {
      console.error('Error convirtiendo WKT a coordenadas:', error);
      return [];
    }
  };

  // Cargar archivos KML asociados a cada faena del reporte específico
  const cargarArchivosKML = async (faena: ReporteFaenaCompleta, _reporteId: number): Promise<any[]> => {
    try {
      // ✅ OPTIMIZADO: Usar archivos_kml que ya vienen del backend en lugar de hacer llamada adicional
      const archivosBase = faena.archivos_kml || [];

      if (archivosBase.length === 0) {
        return []; // No hay KMLs para esta faena
      }

      // Cargar polígonos y archivo original de cada KML para el mapa
      const archivosConPoligonos = await Promise.all(
        archivosBase.map(async (archivo: ArchivoKmlCompleto) => {
          try {
            // Obtener polígonos del archivo KML
            const poligonosResponse = await ApiClient.get(`/kml/${archivo.id}/poligonos`);
            const poligonos = poligonosResponse as any;
            
            // ✅ CORREGIDO: Descargar archivo KML original para contexto completo
            let kmlFileData = null;
            try {
              // Usar fetch directo para descargar archivos binarios
              const baseURL = ApiClient.getBaseURL();
              const token = ApiClient.getAuthToken();
              const empresaId = ApiClient.getEmpresaId();
              
              const response = await fetch(`${baseURL}/kml/${archivo.id}/download`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'X-Empresa-ID': empresaId || '1',
                },
              });
              
              if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
              }
              
              const kmlBlob = await response.blob();
              
              // Crear un File object desde el Blob para compatibilidad
              kmlFileData = new File([kmlBlob], archivo.nombre_archivo_original, {
                type: 'application/vnd.google-earth.kml+xml'
              });
              
              // console.log(`✅ KML descargado para contexto: ${archivo.nombre_archivo_original} (${kmlFileData.size} bytes)`);
            } catch (error) {
              console.error(`❌ Error descargando KML ${archivo.id}:`, error);
            }
            
            return {
              id: archivo.id,
              archivo_id: archivo.id,
              nombre_original: archivo.nombre_archivo_original,
              area_hectareas: archivo.area_hectareas,
              longitud_metros: archivo.longitud_total_metros || 0,
              file: kmlFileData, // ✅ CORREGIDO: Incluir archivo real descargado
              processed: true,
              poligonos: poligonos.map((poligono: any) => ({
                id: poligono.id,
                nombre: poligono.nombre,
                descripcion: poligono.descripcion,
                area_hectareas: poligono.area_hectareas,
                coordinates: convertirWKTACoordenadas(poligono.geometria),
                orden: poligono.orden
              }))
            };
          } catch (error) {
            console.error(`❌ Error cargando polígonos para archivo ${archivo.nombre_archivo_original}:`, error);
            // Retornar archivo sin polígonos si falla
            return {
              id: archivo.id,
              archivo_id: archivo.id,
              nombre_original: archivo.nombre_archivo_original,
              area_hectareas: archivo.area_hectareas,
              longitud_metros: archivo.longitud_total_metros || 0,
              file: null,
              processed: true,
              poligonos: [] // Sin polígonos si falla
            };
          }
        })
      );
      
      return archivosConPoligonos;

    } catch (error) {
      console.error(`❌ Error procesando archivos KML para ReporteFaena ${faena.id}:`, error);
      return [];
    }
  };

  // Efecto para pre-cargar datos del reporte en el contexto
  useEffect(() => {
    if (reporte && !datosPreCargados) {      
      const transformarReporteParaContexto = async () => {
        try {
          // Transformar faenas
          const faenasTransformadas = await Promise.all(
            reporte.faenas.map(async (faena: any, index: number) => {
              // Cargar archivos KML si existen
              const archivosKml = await cargarArchivosKML(faena, reporteId);

              return {
                id_temporal: `faena_${faena.id}_${Date.now()}_${index}`,
                faena_id: faena.faena_id,
                predio_id: faena.predio_id || null,
                rodal_id: faena.rodal_id || null,
                horas: faena.horas,
                area_trabajada: faena.area || 0,
                unidad_medida: 'ha',
                descripcion: faena.faena_descripcion || `Faena ${faena.faena_id}`,
                faena_codigo: faena.faena_codigo || '',
                predio_codigo: faena.predio_codigo || '',
                rodal_codigo: faena.rodal_codigo || '',
                archivos_kml: archivosKml,
                area_kml_total: archivosKml.reduce((total: number, kml: any) => total + (kml.area_hectareas || 0), 0),
                longitud_kml_total: archivosKml.reduce((total: number, kml: any) => total + (kml.longitud_metros || 0), 0),
                calificadores: (faena.calificadores || []).map((c: any) => ({ calificador_id: c.calificador_id, valor: c.valor })),
                observaciones: '',
              };
            })
          );

          // Transformar insumos
          const insumosTransformados = reporte.insumos.map((insumo: any, index: number) => ({
            id_temporal: `insumo_${insumo.id}_${Date.now()}_${index}`,
            insumo_id: insumo.insumo_id,
            nombre_insumo: insumo.insumo_descripcion || `Insumo ${insumo.insumo_id}`,
            cantidad: insumo.cantidad,
            unidad: insumo.unidad_medida || 'kg',
            costo_unitario: 0, // TODO: Agregar costo_unitario al backend si es necesario
          }));

          // Transformar items de chequeo
          const itemsChequeoTransformados = reporte.items_chequeo.map((item: any, index: number) => ({
            id_temporal: `item_${item.id}_${Date.now()}_${index}`,
            item_chequeo_id: item.itemchequeo_id,
            nombre_item: item.item_descripcion || `Item ${item.itemchequeo_id}`,  // ✅ CORREGIDO: Usar campo plano
            verificado: true, // Si está en el reporte, se asume verificado
            horometro: item.horometro,
            tiempo: item.tiempo,
            observaciones: item.observaciones || '',
            requiere_atencion: false,  // ✅ SIMPLIFICADO: El backend no envía este campo en el response
          }));

          // Cargar todos los datos en el contexto
          dispatch(reporteActions.inicializar({
            numero_reporte: reporte.numero,
            maquina_id: reporte.maquina_id,
            maquina_info: reporte.maquina ? {
              codigo: reporte.maquina.codigo,
              tipo: reporte.maquina.tipo,
              descripcion: reporte.maquina.descripcion,
            } : undefined,
            fecha_reporte: reporte.fecha.split('T')[0], // Solo la fecha, sin hora
            operador_id: reporte.operador_id || null,
            operador_info: reporte.operador ? {
              codigo: reporte.operador.codigo,
              nombre: reporte.operador.nombre,
            } : undefined,
            horometro_inicial: reporte.horometro_inicial,
            horometro_final: reporte.horometro_final,
            horas_trabajadas: reporte.horometro_final - reporte.horometro_inicial,
            faenas: faenasTransformadas,
            insumos: insumosTransformados,
            items_chequeo: itemsChequeoTransformados,
            estado_maquina: reporte.estado_maquina as any || 'OPERATIVA',
            observaciones: reporte.observaciones || '',
            novedades: reporte.novedades || '',
            estado_reporte: 'completo', // Ya está guardado en BD
          }));

          setReporteOriginal(reporte);
          setDatosPreCargados(true);
          
          toast.success('Reporte cargado para edición');
          
        } catch (error) {
          console.error('Error transformando reporte:', error);
          toast.error('Error al cargar datos del reporte');
        }
      };

      transformarReporteParaContexto();
    }
  }, [reporte, datosPreCargados, dispatch]);

  // Verificar permisos de edición UAU
  const puedeEditar = () => {
    if (!reporte || !usuario) return false;
    
    // No permitir editar reportes aprobados
    if (reporte.estado === 'APROBADO') return false;
    
    // 1. Administradores pueden editar cualquier reporte
    if (usuario.tipo_usuario === 'administrador') return true;
    
    // 2. Verificar permiso general de edición UAU
    if (usuario.permisos_menu?.includes('reportes.editar')) return true;
    
    // 3. Supervisores pueden editar reportes de su área
    if (usuario.tipo_usuario === 'supervisor') return true;
    
    // 4. Operadores: verificar permisos específicos por máquina
    if (usuario.tipo_usuario === 'operador') {
      // TODO: Verificar asignación específica de la máquina
      // Por ahora, usar lógica simplificada: solo sus propios reportes
      
      // Verificar si es el creador (mapeo UAU -> legacy)
      const esCreador = reporte.creado_por === usuario.id ||
                       reporte.creado_por_uau_user_id === (usuario as any).user_id;
      
      if (esCreador) {
        // Es el creador: verificar si puede editar sus propios reportes de esta máquina
        // Por ahora retornar true, pero se debería verificar asignación específica
        return true;
      }
      
      // No es el creador: verificar si puede editar reportes de otros en esta máquina
      // Por ahora retornar false, pero se debería verificar asignación específica
      return false;
    }
    
    return false;
  };

  // Limpiar contexto al salir
  useEffect(() => {
    return () => {
      if (datosPreCargados) {
        dispatch(reporteActions.limpiar());
      }
    };
  }, [datosPreCargados, dispatch]);

  // Limpiar contexto al salir del componente
  useEffect(() => {
    return () => {
      dispatch(reporteActions.limpiar());
      
      // 🆕 NUEVO: Invalidar cache de React Query para forzar recarga
      // Esto evita que se use data en cache al volver a editar
      if (typeof window !== 'undefined' && window.location.pathname.includes('/reportes/')) {
        // Marcar que se debe invalidar el cache del reporte
        localStorage.setItem(`invalidate-reporte-${reporteId}`, 'true');
      }
    };
  }, [dispatch, reporteId]);

  if (loadingReporte) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
          <span className="ml-2 text-gray-600">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (errorReporte || !reporte) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <ErrorState 
          message="Error al cargar el reporte" 
          actionText="Volver a Reportes"
          onAction={() => navigate('/reportes')}
        />
      </div>
    );
  }

  if (!puedeEditar()) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <ErrorState 
          message={reporte.estado === 'APROBADO' 
            ? "No se pueden editar reportes aprobados" 
            : "No tienes permisos para editar este reporte"
          }
          actionText="Ver Reporte"
          onAction={() => navigate(`/reportes/${reporteId}`)}
        />
      </div>
    );
  }

  if (!datosPreCargados) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
          <span className="ml-2 text-gray-600">{t('common.processing')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header con información del reporte */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('reports.edit_report')} #{reporte.numero}
          </h1>
          <p className="text-gray-600">
            Máquina: {reporte.maquina?.codigo} - {reporte.maquina?.descripcion} | 
            Fecha: {formatDateSafe(reporte.fecha)} |
            Estado: <span className={`font-medium ${
              reporte.estado === 'PENDIENTE' ? 'text-yellow-600' : 
              reporte.estado === 'APROBADO' ? 'text-green-600' : 'text-gray-600'
            }`}>{reporte.estado}</span>
          </p>
        </div>
        
        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/reportes/${reporteId}`)}
            title="Ver vista completa de solo lectura del reporte"
          >
            {t('reports.report_detail')}
          </Button>
          
          <Button
            variant="outline"
            onClick={() => navigate('/reportes')}
          >
            {t('common.back')}
          </Button>
        </div>
      </div>

      {/* Indicador de modo edición */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-blue-700">{t('reports.edit_report')}</p>
          </div>
        </div>
      </div>

      {/* Formulario completo reutilizado */}
      <Card>
        <Card.Content>
          <FormularioReporteCompleto 
            modoEdicion={true}
            reporteId={reporteId}
            refetchReporte={refetchReporte}  // 🆕 NUEVO: Pasar función de refetch
          />
        </Card.Content>
      </Card>
    </div>
  );
};

export default EditarReporteView;